import { useState } from "react";
import {
  Toast,
  TOAST_VARIANTS,
  TOAST_VISIBILITY_DURATIONS,
  ToastPositions,
  ToastVariants,
  ToastVisibilityDurations,
} from "@cdkglobal/radial";
import { uniqueId } from "underscore";
import { useIntl } from "react-intl";
import { useTranslation } from "react-i18next";
import { translateDynamicValidationMessage } from "../LandingPage/helpers";

type ToasterProps = {
  variant: ToastVariants;
  message: string | string[];
  clearMessage: () => void;
  position?: ToastPositions;
  actionText?: string;
  noActionButton?: boolean;
  style?: React.CSSProperties;
  visibilityDuration?: ToastVisibilityDurations;
};

const toastContent = (message: string | string[]) => (
  <div>
    {Array.isArray(message) ? (
      <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
        {message.map((msg) => (
          <li key={uniqueId(msg)}>{msg}</li>
        ))}
      </ul>
    ) : (
      <p style={{ margin: 0 }}>{message}</p>
    )}
  </div>
);

const Toaster = (props: ToasterProps): JSX.Element => {
  const [showToast, setShowToast] = useState(true);
  const {
    variant,
    message,
    actionText,
    position,
    noActionButton,
    visibilityDuration,
    clearMessage,
  } = props;
  const intl = useIntl();
  const { t, i18n } = useTranslation();

  const translatedMessage = i18n.language?.startsWith("en")
    ? message
    : translateDynamicValidationMessage(message, t);

  let toastVariant: ToastVariants;
  let defaultVisibilityDuration;

  switch (variant) {
    case "negative":
      toastVariant = TOAST_VARIANTS.NEGATIVE;
      defaultVisibilityDuration = TOAST_VISIBILITY_DURATIONS.INFINITE;
      break;
    case "positive":
      toastVariant = TOAST_VARIANTS.POSITIVE;
      defaultVisibilityDuration = TOAST_VISIBILITY_DURATIONS.SIX_SECONDS;
      break;
    case "warning":
      toastVariant = TOAST_VARIANTS.WARNING;
      defaultVisibilityDuration = TOAST_VISIBILITY_DURATIONS.INFINITE;
      break;
    default:
      toastVariant = TOAST_VARIANTS.NEUTRAL;
      defaultVisibilityDuration = TOAST_VISIBILITY_DURATIONS.INFINITE;
      break;
  }

  const handleClose = () => {
    setShowToast(false);
    clearMessage?.();
  };

  return (
    <>
      {showToast && (
        <Toast
          id="cdcm-toast"
          actions={
            noActionButton
              ? []
              : [
                  {
                    onClick: handleClose,
                    text:
                      actionText ??
                      intl.formatMessage({
                        id: "button.close",
                        defaultMessage: "Close",
                      }),
                  },
                ]
          }
          variant={toastVariant}
          content={toastContent(translatedMessage)}
          position={position ?? "inline"}
          visibilityDuration={visibilityDuration ?? defaultVisibilityDuration}
          onVisibilityDurationEnd={handleClose}
          style={props.style}
        />
      )}
    </>
  );
};

export default Toaster;
